<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.builder.hero.title')"
      :paragraph="t('jc.view.builder.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.builder.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.builder.hero.float.alt'),
      }"
      :label="t('jc.view.builder.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.builder.ourOffer.title')"
      :paragraph="t('jc.view.builder.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.builder.ourConditions.title')"
      :paragraph="t('jc.view.builder.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.builder.yourTasks.title')"
      :paragraph="t('jc.view.builder.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.builder.impressions.title')"
      :paragraph="t('jc.view.builder.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.builder.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.builder.howToApply.title')"
      :paragraph="t('jc.view.builder.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.builder.moreInformation.title')"
        :paragraph="t('jc.view.builder.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/builder.png'

definePageMeta({
  nameKey: 'participate-builder',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.builder.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'dragon',
    text: t('jc.view.builder.ourOffer.iconHighlight.creativeDevelopment.text'),
  },
  {
    icon: 'brain-circuit',
    text: t('jc.view.builder.ourOffer.iconHighlight.newTechniques.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.builder.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: 'wrench',
    text: t('jc.view.builder.ourOffer.iconHighlight.buildTools.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.builder.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.builder.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.builder.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.builder.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.builder.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'parachute-box',
    text: t('jc.view.builder.ourOffer.icon.provideTools.text'),
  },
  {
    icon: 'paintbrush-pencil',
    text: t('jc.view.builder.ourOffer.icon.passionatePeople.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.builder.ourOffer.icon.serverPerformance.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.builder.ourOffer.icon.teamTools.text'),
  },
  {
    icon: 'people-arrows-left-right',
    text: t('jc.view.builder.ourOffer.icon.feedback.text'),
  },
  {
    icon: 'people-carry-box',
    text: t('jc.view.builder.ourOffer.icon.teamBuilding.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.builder.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'alicorn',
    text: t('jc.view.builder.ourConditions.icon.fantasy.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.builder.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.builder.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.builder.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.builder.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'gopuram',
    text: t('jc.view.builder.yourTasks.icon.create.text'),
  },
  {
    icon: 'mountains',
    text: t('jc.view.builder.yourTasks.icon.design.text'),
  },
  {
    icon: 'magnifying-glass-plus',
    text: t('jc.view.builder.yourTasks.icon.furnish.text'),
  },
  {
    icon: 'hand-holding-heart',
    text: t('jc.view.builder.yourTasks.icon.breathe.text'),
  },
  {
    icon: 'map-location',
    text: t('jc.view.builder.yourTasks.icon.plan.text'),
  },
  {
    icon: 'earth-americas',
    text: t('jc.view.builder.yourTasks.icon.construct.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'decostand',
    mediaType: 'image/*',
    src: 'images/impressions/builder/decostand_editor.png',
  },
  {
    identifier: 'tools',
    mediaType: 'image/*',
    src: 'images/impressions/builder/tools_and_tipps.png',
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.builder.howToApply.icon.firstContact.title'),
    text: t('jc.view.builder.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.builder.howToApply.icon.conversation.title'),
    text: t('jc.view.builder.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.builder.howToApply.icon.wait.title'),
    text: t('jc.view.builder.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.builder.howToApply.icon.trainee.title'),
    text: t('jc.view.builder.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.builder.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.builder.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.builder.moreInformation.card.team.text'),
    label: t('jc.view.builder.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.builder.moreInformation.card.weekly_review.text'),
    label: t('jc.view.builder.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.builder.moreInformation.card.plan.text'),
    label: t('jc.view.builder.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.builder.moreInformation.card.stackShare.text'),
    label: t('jc.view.builder.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.builder.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.builder.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.builder.moreInformation.card.blog.text'),
    label: t('jc.view.builder.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
